import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isAfter, isBefore } from 'date-fns';
import { AssignedStatus } from '@constants/conversations';
import { conversationsActions } from '@actions';

export const useConversations = () => {
    const { data } = useSelector(state => state.conversations);
    const currentUser = useSelector(state => state.auth.user);
    const conversationsRef = useRef([]);

    const dispatch = useDispatch();

    useEffect(() => {
        conversationsRef.current = data;
    }, [data]);

    const removeConversationById = id => {
        const filteredConversations = conversationsRef.current.filter(c => c.id !== id);
        dispatch(conversationsActions.update(filteredConversations));
    };

    const readConversationById = id => {
        const conversationsCopy = conversationsRef.current.slice();
        const updatedConversationIndex = conversationsCopy.findIndex(c => c.id === id);
        if (updatedConversationIndex !== -1) {
            conversationsCopy[updatedConversationIndex].read = true;
            dispatch(conversationsActions.update(conversationsCopy));
        }
    };

    const isConversationMatchesFilter = (conversation, filter) => {
        const { locations, channelId, status, assignedUserId, createdAt } = conversation;
        const isFilterMine = filter.assignment === AssignedStatus.Mine;
        const isFilterUnassigned = filter.assignment === AssignedStatus.Unassigned;

        // INFO: check locations
        if (locations.length > 0 && !locations.some(locationId => filter.locations.includes(locationId))) {
            return false;
        }
        // INFO: check channels
        if (!filter.channels.includes(channelId)) {
            return false;
        }
        // INFO: check status
        if (!filter.statuses.includes(status)) {
            return false;
        }
        // INFO: check assignment
        if ((isFilterMine && assignedUserId !== currentUser?.id) || (isFilterUnassigned && assignedUserId !== null)) {
            return false;
        }
        // INFO: check createdAt
        const hasFilterDate = filter.timeframe.start && filter.timeframe.end;
        const createdAtDate = new Date(createdAt);
        const filterStartDate = new Date(filter.timeframe.start);
        const filterEndDate = new Date(filter.timeframe.end);
        if (hasFilterDate && (!isAfter(createdAtDate, filterStartDate) || !isBefore(createdAtDate, filterEndDate))) {
            return false;
        }

        return true;
    };

    const updateConversationInFeedAndReturn = (conversation, filter, withUpdateCounters = false) => {
        // INFO: skip or remove conversation if new and it doesn't match filter and update counters
        if (!isConversationMatchesFilter(conversation, filter)) {
            const conversationIndex = conversationsRef.current.findIndex(c => c.id === conversation?.id);
            if (conversationIndex !== -1) {
                removeConversationById(conversation?.id);
            }
            dispatch(conversationsActions.getCounters(filter));
            return true;
        }

        const conversationIndex = conversationsRef.current.findIndex(c => c.id === conversation?.id);
        if (conversationIndex !== -1) {
            // INFO: update conversation in feed if it exists
            const updatedConversations = conversationsRef.current.slice();
            updatedConversations[conversationIndex] = { ...updatedConversations[conversationIndex], ...conversation };
            dispatch(conversationsActions.update(updatedConversations));
            // INFO: update counters if required (assign, solve, etc)
            if (withUpdateCounters) {
                dispatch(conversationsActions.getCounters(filter));
            }
            return true;
        }
        // INFO: return false to trigger return from function
        return false;
    };

    const updateConversationsChannelData = (channelId, channelStatus, isDeleted) => {
        const updatedConversations = conversationsRef.current.map(conversation => {
            return conversation.channelId === channelId
                ? { ...conversation, channelStatus, channelId: isDeleted ? null : channelId }
                : conversation;
        });
        dispatch(conversationsActions.update(updatedConversations));
    };

    return {
        readConversationById,
        updateConversationInFeedAndReturn,
        updateConversationsChannelData,
        conversationsRef,
    };
};
